import * as React from "react";

export interface TabStripScroll {
  stripRef: React.RefObject<HTMLDivElement | null>;
  showLeft: boolean;
  showRight: boolean;
  scrollBy: (delta: number) => void;
  /** attach to the strip's onClickCapture to swallow clicks that were drags */
  onClickCapture: (e: React.MouseEvent) => void;
}

/**
 * Drag-to-scroll + fade indicators for a horizontal tab strip.
 */
export const useTabStripScroll = (enabled: boolean): TabStripScroll => {
  const stripRef = React.useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = React.useState(false);
  const [showRight, setShowRight] = React.useState(false);
  const movedRef = React.useRef(false);

  const updateFades = React.useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    setShowLeft(strip.scrollLeft > 2);
    setShowRight(strip.scrollLeft < max - 2);
  }, []);

  React.useEffect(() => {
    const strip = stripRef.current;
    if (!enabled || !strip) return;

    updateFades();
    const raf = requestAnimationFrame(updateFades);

    let down = false;
    let startX = 0;
    let startScroll = 0;

    const onDown = (e: MouseEvent) => {
      down = true;
      movedRef.current = false;
      startX = e.pageX;
      startScroll = strip.scrollLeft;
    };
    const onMove = (e: MouseEvent) => {
      if (!down) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 3) {
        movedRef.current = true;
        strip.style.cursor = "grabbing";
      }
      strip.scrollLeft = startScroll - dx;
    };
    const onUp = () => {
      down = false;
      strip.style.cursor = "";
    };

    strip.addEventListener("scroll", updateFades, { passive: true });
    window.addEventListener("resize", updateFades);
    strip.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      cancelAnimationFrame(raf);
      strip.removeEventListener("scroll", updateFades);
      window.removeEventListener("resize", updateFades);
      strip.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [enabled, updateFades]);

  const scrollBy = React.useCallback((delta: number) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollLeft += delta;
    updateFades();
  }, [updateFades]);

  const onClickCapture = React.useCallback((e: React.MouseEvent) => {
    if (movedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      movedRef.current = false;
    }
  }, []);

  return { stripRef, showLeft, showRight, scrollBy, onClickCapture };
};
